"use client"
import { Link } from "react-router-dom"
import Footer from "./Footer"

const AboutUs = () => {
  return (
    <div className="about-us-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              About <span className="hero-highlight">Shopify Scraper</span>
            </h1>
            <p className="hero-description">
              We're passionate about helping e-commerce entrepreneurs and developers streamline their workflow with
              powerful, easy-to-use tools.
            </p>
          </div>
          <div className="hero-image">
            <img src="/images/logo.png" alt="About Shopify Scraper" className="hero-illustration" />
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="story-section">
        <div className="story-container">
          <div className="story-content">
            <h2 className="section-title">Our Story</h2>
            <p className="story-text">
              Founded in 2023, Shopify Scraper was born from a simple observation: successful Shopify store owners were
              spending countless hours manually copying products from competitor stores. We knew there had to be a
              better way.
            </p>
            <p className="story-text">
              Our team of experienced developers and e-commerce experts came together to create a solution that would
              revolutionize how online retailers research and source products. What started as a simple Chrome extension
              has evolved into a comprehensive tool trusted by thousands of entrepreneurs worldwide.
            </p>
            <p className="story-text">
              Today, Shopify Scraper helps store owners save hundreds of hours while building their product catalogs,
              allowing them to focus on what matters most: growing their business and serving their customers.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="mission-section">
        <div className="mission-container">
          <div className="mission-grid">
            <div className="mission-card">
              <div className="mission-icon">🎯</div>
              <h3 className="mission-title">Our Mission</h3>
              <p className="mission-description">
                To empower e-commerce entrepreneurs with innovative tools that simplify product research and catalog
                management, enabling them to build successful online stores faster than ever before.
              </p>
            </div>
            <div className="mission-card">
              <div className="mission-icon">🚀</div>
              <h3 className="mission-title">Our Vision</h3>
              <p className="mission-description">
                To become the leading platform for e-commerce automation tools, helping millions of entrepreneurs
                worldwide achieve their business goals through technology and innovation.
              </p>
            </div>
            <div className="mission-card">
              <div className="mission-icon">💡</div>
              <h3 className="mission-title">Our Values</h3>
              <p className="mission-description">
                Innovation, reliability, and user-centric design drive everything we do. We believe in creating tools
                that are powerful yet simple, efficient yet ethical.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="team-container">
          <h2 className="section-title">Meet Our Team</h2>
          <p className="team-intro">
            We're a diverse group of developers, designers, and e-commerce experts united by our passion for innovation.
          </p>
          <div className="team-grid">
            <div className="team-member">
              <div className="member-avatar">👨‍💻</div>
              <h4 className="member-name">Alex Johnson</h4>
              <p className="member-role">Founder & CEO</p>
              <p className="member-bio">
                Former Shopify developer with 8+ years of e-commerce experience. Passionate about building tools that
                solve real problems.
              </p>
            </div>
            <div className="team-member">
              <div className="member-avatar">👩‍🎨</div>
              <h4 className="member-name">Sarah Chen</h4>
              <p className="member-role">Lead Designer</p>
              <p className="member-bio">
                UX/UI expert focused on creating intuitive interfaces that make complex tasks simple and enjoyable.
              </p>
            </div>
            <div className="team-member">
              <div className="member-avatar">👨‍🔧</div>
              <h4 className="member-name">Mike Rodriguez</h4>
              <p className="member-role">Lead Developer</p>
              <p className="member-bio">
                Full-stack developer specializing in browser extensions and data processing systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="why-choose-container">
          <div className="why-choose-content">
            <h2 className="section-title">Why Choose Shopify Scraper?</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <span className="benefit-check">✓</span>
                <div>
                  <h4>Proven Track Record</h4>
                  <p>Trusted by 10,000+ store owners worldwide</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-check">✓</span>
                <div>
                  <h4>Continuous Innovation</h4>
                  <p>Regular updates and new features based on user feedback</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-check">✓</span>
                <div>
                  <h4>Dedicated Support</h4>
                  <p>Responsive customer service and comprehensive documentation</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-check">✓</span>
                <div>
                  <h4>Ethical Practices</h4>
                  <p>We respect website terms of service and promote fair use</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Ready to join our community?</h2>
          <p className="cta-description">
            Discover why thousands of entrepreneurs trust Shopify Scraper for their business growth
          </p>
          <div className="cta-buttons">
            <Link to="/" className="cta-button primary">
              Get Started Today
            </Link>
            <Link to="/contact" className="cta-button secondary">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Styling */}
      <style jsx="true">{`
        /* Hero Section */
        .hero-section {
          padding: 4rem 0;
          background-color: #fff;
        }
        
        .hero-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .hero-content {
          flex: 1;
          max-width: 600px;
        }
        
        .hero-title {
          font-size: 2.5rem;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          color: #1a202c;
        }
        
        .hero-highlight {
          color: #007BFF; /* Changed from #00a99d to blue */
        }
        
        .hero-description {
          margin-bottom: 1.5rem;
          font-size: 1.125rem;
          line-height: 1.6;
          color: #4a5568;
        }
        
        .hero-image {
          flex: 1;
          display: flex;
          justify-content: center;
        }
        
        .hero-illustration {
          max-width: 300px;
          height: auto;
        }

        /* Story Section */
        .story-section {
          padding: 4rem 0;
          background-color: #f9fafb;
        }
        
        .story-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .section-title {
          font-size: 2.25rem;
          font-weight: 700;
          margin-bottom: 2rem;
          color: #1a202c;
          text-align: center;
        }
        
        .story-text {
          font-size: 1.125rem;
          line-height: 1.7;
          color: #4a5568;
          margin-bottom: 1.5rem;
        }

        /* Mission Section */
        .mission-section {
          padding: 4rem 0;
          background-color: #fff;
        }
        
        .mission-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .mission-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-top: 2rem;
        }
        
        .mission-card {
          text-align: center;
          padding: 2rem;
          background-color: #f9fafb;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        
        .mission-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }
        
        .mission-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: #1a202c;
        }
        
        .mission-description {
          font-size: 1rem;
          line-height: 1.6;
          color: #4a5568;
        }

        /* Team Section */
        .team-section {
          padding: 4rem 0;
          background-color: #f9fafb;
        }
        
        .team-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
          text-align: center;
        }
        
        .team-intro {
          font-size: 1.125rem;
          color: #4a5568;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }
        
        .team-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }
        
        .team-member {
          background-color: #fff;
          padding: 2rem;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        
        .member-avatar {
          font-size: 4rem;
          margin-bottom: 1rem;
        }
        
        .member-name {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #1a202c;
        }
        
        .member-role {
          color: #007BFF; /* Changed from #00a99d to blue */
          font-size: 1rem;
          font-weight: 500;
          margin-bottom: 1rem;
        }
        
        .member-bio {
          font-size: 0.875rem;
          line-height: 1.6;
          color: #4a5568;
        }

        /* Why Choose Section */
        .why-choose-section {
          padding: 4rem 0;
          background-color: #fff;
        }
        
        .why-choose-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }
        
        .benefit-item {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
        }
        
        .benefit-check {
          color: #007BFF; /* Changed from #00a99d to blue */
          font-weight: bold;
          font-size: 1.25rem;
          flex-shrink: 0;
        }
        
        .benefit-item h4 {
          font-size: 1.125rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #1a202c;
        }
        
        .benefit-item p {
          font-size: 1rem;
          color: #4a5568;
          line-height: 1.6;
        }

        /* CTA Section */
        .cta-section {
          padding: 4rem 0;
          background-color: #007BFF; /* Changed from #00a99d to blue */
          color: white;
        }
        
        .cta-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
          text-align: center;
        }
        
        .cta-title {
          font-size: 2.25rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }
        
        .cta-description {
          font-size: 1.125rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }
        
        .cta-buttons {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
        }
        
        .cta-button {
          padding: 0.75rem 1.5rem;
          border-radius: 0.375rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
        }
        
        .cta-button.primary {
          background-color: white;
          color: #007BFF; /* Changed from #00a99d to blue */
        }
        
        .cta-button.primary:hover {
          background-color: #f9fafb;
        }
        
        .cta-button.secondary {
          background-color: transparent;
          border: 2px solid white;
          color: white;
        }
        
        .cta-button.secondary:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        /* Responsive Styles */
        @media (max-width: 1024px) {
          .hero-container {
            flex-direction: column;
            text-align: center;
          }
          
          .hero-content {
            max-width: 100%;
            margin-bottom: 2rem;
          }
          
          .benefits-grid {
            grid-template-columns: 1fr;
          }
        }
        
        @media (max-width: 768px) {
          .hero-title {
            font-size: 2rem;
          }
          
          .section-title, .cta-title {
            font-size: 1.75rem;
          }
          
          .mission-grid, .team-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}

export default AboutUs